import classnames from 'classnames'
import { assign, filter, get, maxBy, minBy, sortBy } from 'lodash'
import PropTypes from 'prop-types'
import { createRef, PureComponent } from 'react'
import { Table } from 'semantic-ui-react'

import { naturalSort } from 'utils'

import Avatar from '../calendar/team/Avatar'
import Name from '../calendar/team/Name'

import './RankingTable.css'

const MIN_WIDTH = 290

const RESIZE_DEBOUNCE_MS = 300

class RankingTable extends PureComponent {
  static classMaxORMin(value, index, stats, reverse = false) {
    const min = minBy(stats, index)
    const max = maxBy(stats, index)

    if (min && max && min[index] !== max[index]) {
      if (reverse) {
        // I know, too much nesting but it worth it
        if (value === max[index]) {
          return 'min'
        }

        return value === min[index] ? 'max' : ''
      }

      if (value === max[index]) {
        return 'max'
      }

      return value === min[index] ? 'min' : ''
    }

    return ''
  }

  componentDidMount() {
    window.addEventListener('resize', this.onResize)
    this.onResize()
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize)
  }

  el = createRef()

  getHeadersRow = (key = null) => {
    const { fairPlay } = this.props
    const { width } = this.state

    return (
      <Table.Row key={`HeadersRow${key || ''}`} className="RankingTable__header__row">
        <Table.HeaderCell title="Posición">#</Table.HeaderCell>
        <Table.HeaderCell colSpan="2" title="Equipo">
          Equipo
        </Table.HeaderCell>
        <Table.HeaderCell title="Puntos">P.</Table.HeaderCell>
        {width >= 320 && <Table.HeaderCell title="Efectividad">Efec</Table.HeaderCell>}
        <Table.HeaderCell title="Partidos Jugados">PJ</Table.HeaderCell>
        {width >= 290 && <Table.HeaderCell title="Partidos Ganados">PG</Table.HeaderCell>}
        {width > 400 && <Table.HeaderCell title="Partidos Empatados">PE</Table.HeaderCell>}
        {(fairPlay && width > 450) || (!fairPlay && width > 300) ? (
          <Table.HeaderCell title="Partidos Perdidos">PP</Table.HeaderCell>
        ) : null}
        {width > 290 && <Table.HeaderCell title="Goles a Favor">GF</Table.HeaderCell>}
        {width > 450 && <Table.HeaderCell title="Goles en Contra">GC</Table.HeaderCell>}
        {width >= 290 && <Table.HeaderCell title="Goles de Diferencia">GD</Table.HeaderCell>}
        {fairPlay && width >= 300 && <Table.HeaderCell title="Juego Límpio">JL</Table.HeaderCell>}
      </Table.Row>
    )
  }

  getSorted = (teamStats) => {
    const { fairPlay, tieBreakersOrder } = this.props
    const tieBreakers = {
      FP: (t) => (t.teams_stats ? -t.teams_stats.fair_play : 0), // Fair Play
      GA: (t) => (t.teams_stats ? -t.teams_stats.goals_against : 0), // Goals Against
      GD: (t) => (t.teams_stats ? -t.teams_stats.goals_difference : 0), // Goals Difference
      GP: (t) => (t.teams_stats ? -t.teams_stats.goals_pro : 0), // Goals Pro
      MI: (t) => (t.teams_stats ? -t.teams_stats.matches_tie : 0), // Matches Tie
      ML: (t) => (t.teams_stats ? -t.teams_stats.matches_lost : 0), // Matches Lost
      MT: (t) => (t.teams_stats ? -t.teams_stats.matches_total : 0), // Matches Total
      MW: (t) => (t.teams_stats ? -t.teams_stats.matches_won : 0), // Matches Won
      S: (t) => (t.teams_stats ? -t.teams_stats.stats_score : 0), // Score
      SE: (t) => (t.teams_stats ? -t.teams_stats.stats_effectiveness : 0), // Stats Effectiveness
    }

    const sorting = [tieBreakers.S]
    const tieBreakersList = tieBreakersOrder || ''

    tieBreakersList.split(',').forEach((tieBreaker) => {
      if (get(tieBreakers, tieBreaker)) {
        sorting.push(get(tieBreakers, tieBreaker))
      }
    })

    if (sorting.length === 2) {
      sorting.push(tieBreakers.GD)
      if (fairPlay) {
        sorting.push(tieBreakers.FP)
      } else {
        sorting.push(tieBreakers.GP)
      }
    }

    return sortBy(teamStats, sorting)
  }

  getTeamRow = (stat, stats) => {
    const { fairPlay, highlightedTeams, printable, widget } = this.props
    const { width } = this.state
    const team = stat.team ? this.props.teams[stat.team.id] : null

    if (!stat || !team) {
      return null
    }

    return (
      <Table.Row
        key={team.id}
        className={classnames('RankingTable__row', highlightedTeams.indexOf(team.id) >= 0 && 'highlight')}
      >
        <Table.Cell textAlign="right">{stat.position}.</Table.Cell>
        <Avatar align="right" printable={printable} team={team} widget={widget} />
        <Name align="left" printable={printable} team={team} useNick={width < 600} widget={widget} />
        <Table.Cell className={RankingTable.classMaxORMin(stat.stats_score, 'stats_score', stats)} textAlign="right">
          {stat.stats_score}
        </Table.Cell>
        {width >= 320 && (
          <Table.Cell
            className={RankingTable.classMaxORMin(stat.stats_effectiveness, 'stats_effectiveness', stats)}
            textAlign="right"
          >
            {stat.stats_effectiveness}%
          </Table.Cell>
        )}
        <Table.Cell textAlign="right">{stat.matches_total}</Table.Cell>
        {width >= 290 && (
          <Table.Cell className={RankingTable.classMaxORMin(stat.matches_won, 'matches_won', stats)} textAlign="right">
            {stat.matches_won}
          </Table.Cell>
        )}
        {width > 400 && <Table.Cell textAlign="right">{stat.matches_tied}</Table.Cell>}
        {((fairPlay && width > 450) || (!fairPlay && width > 300)) && (
          <Table.Cell
            className={RankingTable.classMaxORMin(stat.matches_lost, 'matches_lost', stats, true)}
            textAlign="right"
          >
            {stat.matches_lost}
          </Table.Cell>
        )}
        {width > 290 && (
          <Table.Cell className={RankingTable.classMaxORMin(stat.goals_pro, 'goals_pro', stats)} textAlign="right">
            {stat.goals_pro}
          </Table.Cell>
        )}
        {width > 450 && (
          <Table.Cell
            className={RankingTable.classMaxORMin(stat.goals_against, 'goals_against', stats, true)}
            textAlign="right"
          >
            {stat.goals_against}
          </Table.Cell>
        )}
        {width >= 290 && (
          <Table.Cell
            className={RankingTable.classMaxORMin(stat.goals_difference, 'goals_difference', stats)}
            textAlign="right"
          >
            {stat.goals_difference}
          </Table.Cell>
        )}
        {fairPlay && width >= 300 && (
          <Table.Cell className={RankingTable.classMaxORMin(stat.fair_play, 'fair_play', stats)} textAlign="right">
            {stat.fair_play}
          </Table.Cell>
        )}
      </Table.Row>
    )
  }

  onResize = () => {
    clearTimeout(this.resizeInterval)
    this.resizeInterval = setTimeout(
      () => this.setState({ width: Math.max(MIN_WIDTH, this.el.current?.offsetWidth || 0) }),
      RESIZE_DEBOUNCE_MS,
    )
  }

  resizeInterval = null

  state = {
    width: MIN_WIDTH,
  }

  render() {
    const { className, fairPlay, printable, stage, teamsStats } = this.props
    const rows = []

    let lastGroup = -1
    let position = 0
    let stageTeamsStats = []

    const parentStage = stage || { teams: [], teams_stats: teamsStats }
    stageTeamsStats = parentStage.teams.length
      ? parentStage.teams.map((team) => ({
          ...team,
          teams_stats: parentStage.teams_stats.find((s) => s.team?.id === team.team?.id),
        }))
      : parentStage.teams_stats.map((stat, index) => ({
          id: stat.id,
          group: '',
          position: index,
          team: stat.team,
          team_name: stat.team?.id,
          teams_stats: stat,
        }))

    stageTeamsStats = this.getSorted(stageTeamsStats)

    let stats = []
    let groupsTotal = 0
    let groupsRowsCount = 0
    stageTeamsStats
      .sort((a, b) => naturalSort(a?.group, b?.group))
      .forEach((team, index) => {
        if (lastGroup !== team.group) {
          lastGroup = team.group
          groupsTotal += 1
          position = 0

          const teams = filter(stageTeamsStats, { group: lastGroup })
          stats = filter(parentStage.teams_stats, (s) => !!teams.find((t) => t.team?.id === s.team?.id))

          rows.push(
            <Table.Row key={lastGroup} className="RankingTable__row group">
              <Table.Cell colSpan={fairPlay ? 13 : 12} disabled>
                Grupo {lastGroup || 'sin definir'}
              </Table.Cell>
            </Table.Row>,
            this.getHeadersRow(lastGroup),
          )
          groupsRowsCount = 0
        }
        if (groupsRowsCount > 0 && groupsRowsCount % 10 === 0) {
          rows.push(this.getHeadersRow(index))
        }

        position += 1
        groupsRowsCount += 1

        const stat = parentStage.teams_stats.find((s) => !!s.team && s.team?.id === team.team?.id)
        if (stat) {
          rows.push(this.getTeamRow(assign({ position }, stat), stats))
        }
      })

    if (groupsTotal === 1) {
      // Remove the group row
      rows.shift()
    }

    return (
      <div className={classnames('RankingTable', className)} ref={this.el}>
        <Table basic celled className="RankingTable__table" selectable={!printable} size="small" unstackable>
          <Table.Body>{rows}</Table.Body>
        </Table>
      </div>
    )
  }
}

RankingTable.propTypes = {
  className: PropTypes.string,
  fairPlay: PropTypes.bool,
  highlightedTeams: PropTypes.array,
  printable: PropTypes.bool,
  stage: PropTypes.object,
  teams: PropTypes.object.isRequired,
  teamsStats: PropTypes.array,
  tieBreakersOrder: PropTypes.string,
  widget: PropTypes.bool,
}

RankingTable.defaultProps = {
  className: null,
  fairPlay: false,
  highlightedTeams: [],
  printable: false,
  stage: null,
  teamsStats: [],
  tieBreakersOrder: '',
  widget: false,
}

export default RankingTable
